import { COMPLIANCE_FRAMEWORK_NAMES, FRAMEWORK_MODEL_IDS } from '../globalEnums'

export interface StringMap {
  [key: string]: Set<string>
}

interface NestedStringMap {
  [key: string]: {
    [key: string]: string
  }
}

const complianceFrameworkObjectiveNames: NestedStringMap = {
  [FRAMEWORK_MODEL_IDS.SOC2]: {
    singular: 'Criterion',
    plural: 'Criteria',
  },
  [FRAMEWORK_MODEL_IDS.TEST]: {
    singular: 'Objective',
    plural: 'Objectives',
  },
  [FRAMEWORK_MODEL_IDS.ISO27001]: {
    singular: 'Control',
    plural: 'Controls',
  },
  [FRAMEWORK_MODEL_IDS.HIPAA]: {
    singular: 'Section',
    plural: 'Sections',
  },
  [FRAMEWORK_MODEL_IDS.PCISAQD]: {
    singular: 'Requirement',
    plural: 'Requirements',
  },
  [FRAMEWORK_MODEL_IDS.PCISAQA]: {
    singular: 'Requirement',
    plural: 'Requirements',
  },
  [FRAMEWORK_MODEL_IDS.HITRUST]: {
    singular: 'BUID',
    plural: 'BUIDs',
  },
  [FRAMEWORK_MODEL_IDS.NISTCSF]: {
    plural: 'Requirements',
    singular: 'Requirement',
  },
  [FRAMEWORK_MODEL_IDS.NYDFS]: {
    plural: 'Requirements',
    singular: 'Requirement',
  },
  [FRAMEWORK_MODEL_IDS.SWIFT_CSCF_A1]: {
    plural: 'Controls',
    singular: 'Control',
  },
  [FRAMEWORK_MODEL_IDS.SWIFT_CSCF_A2]: {
    plural: 'Controls',
    singular: 'Control',
  },
  [FRAMEWORK_MODEL_IDS.SWIFT_CSCF_A3]: {
    plural: 'Controls',
    singular: 'Control',
  },
  [FRAMEWORK_MODEL_IDS.SWIFT_CSCF_A4]: {
    plural: 'Controls',
    singular: 'Control',
  },
  [FRAMEWORK_MODEL_IDS.SWIFT_CSCF_B]: {
    plural: 'Controls',
    singular: 'Control',
  },
  [FRAMEWORK_MODEL_IDS.SWIFT_PSCF]: {
    plural: 'Controls',
    singular: 'Control',
  },
  [FRAMEWORK_MODEL_IDS.TISAX_VDA_ISA]: {
    plural: 'Controls',
    singular: 'Control',
  },
  [FRAMEWORK_MODEL_IDS.NIST800171]: {
    plural: 'Controls',
    singular: 'Control',
  },
  [FRAMEWORK_MODEL_IDS.CMMC_LEVEL_1]: {
    plural: 'Controls',
    singular: 'Control',
  },
  [FRAMEWORK_MODEL_IDS.CMMC_LEVEL_2]: {
    plural: 'Controls',
    singular: 'Control',
  },
  [FRAMEWORK_MODEL_IDS.ISO27018]: {
    plural: 'Controls',
    singular: 'Control',
  },
  default: {
    singular: 'Objective',
    plural: 'Objectives',
  },
}

export const getComplianceFrameworkName = (
  complianceFrameworkModelId: string,
  complianceFrameworkName?: string,
): string => {
  switch (complianceFrameworkModelId) {
    case FRAMEWORK_MODEL_IDS.SOC2:
      return 'SOC 2'
    case FRAMEWORK_MODEL_IDS.SOC1:
      return 'SOC 1'
    case FRAMEWORK_MODEL_IDS.ISO27001:
      return 'ISO 27001'
    case FRAMEWORK_MODEL_IDS.HIPAA:
      return 'HIPAA'
    case FRAMEWORK_MODEL_IDS.PCISAQD:
      return 'PCI DSS SAQ D'
    case FRAMEWORK_MODEL_IDS.PCISAQA:
      return 'PCI DSS SAQ A'
    case FRAMEWORK_MODEL_IDS.HITRUST:
      return 'HITRUST e1'
    case FRAMEWORK_MODEL_IDS.NISTCSF:
      return 'NIST CSF'
    case FRAMEWORK_MODEL_IDS.NYDFS:
      return 'NYDFS'
    case FRAMEWORK_MODEL_IDS.ISO27701_DC:
      return 'ISO 27701 - Data Controller'
    case FRAMEWORK_MODEL_IDS.ISO27701_DP:
      return 'ISO 27701 - Data Processor'
    case FRAMEWORK_MODEL_IDS.GDPR_DC:
      return 'GDPR - Data Controller'
    case FRAMEWORK_MODEL_IDS.GDPR_DP:
      return 'GDPR - Data Processor'
    case FRAMEWORK_MODEL_IDS.PIPEDA_DC:
      return 'PIPEDA - Data Controller'
    case FRAMEWORK_MODEL_IDS.PIPEDA_DP:
      return 'PIPEDA - Data Processor'
    case FRAMEWORK_MODEL_IDS.TEST:
      return 'Test Compliance Framework'
    case FRAMEWORK_MODEL_IDS.SWIFT_CSCF_A1:
      return 'Swift CSCF A1'
    case FRAMEWORK_MODEL_IDS.SWIFT_CSCF_A2:
      return 'Swift CSCF A2'
    case FRAMEWORK_MODEL_IDS.SWIFT_CSCF_A3:
      return 'Swift CSCF A3'
    case FRAMEWORK_MODEL_IDS.SWIFT_CSCF_A4:
      return 'Swift CSCF A4'
    case FRAMEWORK_MODEL_IDS.SWIFT_CSCF_B:
      return 'Swift CSCF B'
    case FRAMEWORK_MODEL_IDS.TISAX_VDA_ISA:
      return 'TISAX VDA ISA'
    case FRAMEWORK_MODEL_IDS.SWIFT_PSCF:
      return 'Swift PSCF'
    case FRAMEWORK_MODEL_IDS.DODCMMC:
      return 'DoD CMMC'
    default:
      return complianceFrameworkName || complianceFrameworkModelId
  }
}

export const getComplianceFrameworkObjectiveName = (
  complianceFrameworkId: string,
  isPlural: boolean,
): string | undefined => {
  const pluralKey = isPlural ? 'plural' : 'singular'
  const response = complianceFrameworkObjectiveNames[complianceFrameworkId]
    ? complianceFrameworkObjectiveNames[complianceFrameworkId][pluralKey]
    : complianceFrameworkObjectiveNames.default[pluralKey]
  return response
}

const splitFrameworkRequirementNameOnSpace = (
  frameworkRequirementName: string,
): string => {
  return frameworkRequirementName.trim().split(' ')[0]
}

const splitFrameworkRequirementNameOnDot = (
  frameworkRequirementName: string,
): string => {
  return frameworkRequirementName.trim().split('.')[0]
}

const splitFrameworkRequirementNameOnSpaceReverse = (
  frameworkRequirementName: string,
): string => {
  const trimmedName = frameworkRequirementName.trim()
  return trimmedName.split(' ')[1] || trimmedName
}

export const COMPLIANCE_FRAMEWORK_NAME_TO_REQUIREMENT_NAME_FUNCTIONS: Record<
  COMPLIANCE_FRAMEWORK_NAMES,
  (frameworkRequirementName: string) => string
> = {
  [COMPLIANCE_FRAMEWORK_NAMES.SOC2]: splitFrameworkRequirementNameOnSpace,
  [COMPLIANCE_FRAMEWORK_NAMES.SOC1]: splitFrameworkRequirementNameOnDot,
  [COMPLIANCE_FRAMEWORK_NAMES.ISO27001]: splitFrameworkRequirementNameOnSpace,
  [COMPLIANCE_FRAMEWORK_NAMES.HIPAA]: splitFrameworkRequirementNameOnSpace,
  [COMPLIANCE_FRAMEWORK_NAMES.PCISAQD]: splitFrameworkRequirementNameOnSpace,
  [COMPLIANCE_FRAMEWORK_NAMES.PCISAQA]: splitFrameworkRequirementNameOnSpace,
  [COMPLIANCE_FRAMEWORK_NAMES.HITRUST]: splitFrameworkRequirementNameOnSpace,
  [COMPLIANCE_FRAMEWORK_NAMES.NISTCSF]: splitFrameworkRequirementNameOnSpace,
  [COMPLIANCE_FRAMEWORK_NAMES.NYDFS]: splitFrameworkRequirementNameOnSpace,
  [COMPLIANCE_FRAMEWORK_NAMES.ISO27701_DC]:
    splitFrameworkRequirementNameOnSpace,
  [COMPLIANCE_FRAMEWORK_NAMES.ISO27701_DP]:
    splitFrameworkRequirementNameOnSpace,
  [COMPLIANCE_FRAMEWORK_NAMES.GDPR_DC]: splitFrameworkRequirementNameOnSpace,
  [COMPLIANCE_FRAMEWORK_NAMES.GDPR_DP]: splitFrameworkRequirementNameOnSpace,
  [COMPLIANCE_FRAMEWORK_NAMES.PIPEDA_DC]: splitFrameworkRequirementNameOnSpace,
  [COMPLIANCE_FRAMEWORK_NAMES.PIPEDA_DP]: splitFrameworkRequirementNameOnSpace,
  [COMPLIANCE_FRAMEWORK_NAMES.TEST]: splitFrameworkRequirementNameOnSpace,
  [COMPLIANCE_FRAMEWORK_NAMES.SWIFT_CSCF_A1]:
    splitFrameworkRequirementNameOnSpace,
  [COMPLIANCE_FRAMEWORK_NAMES.SWIFT_CSCF_A2]:
    splitFrameworkRequirementNameOnSpace,
  [COMPLIANCE_FRAMEWORK_NAMES.SWIFT_CSCF_A3]:
    splitFrameworkRequirementNameOnSpace,
  [COMPLIANCE_FRAMEWORK_NAMES.SWIFT_CSCF_A4]:
    splitFrameworkRequirementNameOnSpace,
  [COMPLIANCE_FRAMEWORK_NAMES.SWIFT_CSCF_B]:
    splitFrameworkRequirementNameOnSpace,
  [COMPLIANCE_FRAMEWORK_NAMES.SWIFT_PSCF]: splitFrameworkRequirementNameOnSpace,
  [COMPLIANCE_FRAMEWORK_NAMES.TISAX_VDA_ISA]:
    splitFrameworkRequirementNameOnSpaceReverse,
  [COMPLIANCE_FRAMEWORK_NAMES.LADMF]: splitFrameworkRequirementNameOnSpace,
  [COMPLIANCE_FRAMEWORK_NAMES.CMMC_LEVEL_1]:
    splitFrameworkRequirementNameOnSpace,
  [COMPLIANCE_FRAMEWORK_NAMES.CMMC_LEVEL_2]:
    splitFrameworkRequirementNameOnSpace,
  [COMPLIANCE_FRAMEWORK_NAMES.ISO27018]: splitFrameworkRequirementNameOnSpace,
  [COMPLIANCE_FRAMEWORK_NAMES.CUSTOM]: splitFrameworkRequirementNameOnSpace,
  [COMPLIANCE_FRAMEWORK_NAMES.ISO27701]: splitFrameworkRequirementNameOnSpace,
  [COMPLIANCE_FRAMEWORK_NAMES.PCIDSS]: splitFrameworkRequirementNameOnSpace,
  [COMPLIANCE_FRAMEWORK_NAMES.GDPR]: splitFrameworkRequirementNameOnSpace,
  [COMPLIANCE_FRAMEWORK_NAMES.CCPA]: splitFrameworkRequirementNameOnSpace,
  [COMPLIANCE_FRAMEWORK_NAMES.NIST800171]: splitFrameworkRequirementNameOnSpace,
  [COMPLIANCE_FRAMEWORK_NAMES.DODCMMC]: splitFrameworkRequirementNameOnSpace,
}
